import React from 'react';
import { graphql } from 'gatsby';
import { Icon } from '@keytrade/components-icon';
import BlockWrapper from '@/components/BlockWrapper';
import { MEDIA_TYPES } from '@/components/media/constants/media.constants';
import { ImageType } from '@/components/Image';
import useTranslations from '@/hooks/useTranslations';
import { Locales } from '@/types/Locales';
import useIsMobile from '@/hooks/useIsMobile';
import useVideo from '@/hooks/useVideo';

import PodcastIcon from './PodcastIcon';

import {
  Media,
  ImageWrapper,
  Thumbnail,
  Img,
  Length,
  ContentWrapper,
  Heading,
  Title,
  TagsWrapper,
  LanguageTag,
  FeaturedTag,
  Description,
  PlayIcon,
} from './MediaItem.styled';

export type MediaItemType = {
  contentful_id: string;
  id: string;
  node_locale: Locales;
  name: string;
  url: string;
  type?: string;
  languages?: string[];
  title?: string;
  description?: {
    description?: string;
  };
  thumbnail?: ImageType;
  length?: string;
  topics?: string[];
  tags?: string[];
};

interface Props {
  item: MediaItemType;
  onVideoClick: React.Dispatch<React.SetStateAction<string>>;
  displayLanguage: boolean;
  isFeatured?: boolean;
  isDark?: boolean;
}

const MediaItem: React.FC<Props> = (props) => {
  const isMobile = useIsMobile();

  const { t } = useTranslations();

  const { item, onVideoClick, displayLanguage, isFeatured, isDark } = props;

  const {
    id,
    description: descriptionObject,
    url,
    type,
    title,
    thumbnail,
    length,
    node_locale,
  } = item;

  const { description } = descriptionObject || {};

  const handleVideoClick = () => {
    onVideoClick(url);
  };

  const isVideo = type === MEDIA_TYPES.VIDEO;
  const { videoThumbnail } = useVideo(isVideo ? url : null);

  return (
    <BlockWrapper id={id} color={isDark ? 'Blue Moon' : 'White'} padding='none'>
      <Media
        isFeatured={isFeatured}
        onClick={isVideo ? handleVideoClick : undefined}
        role={isVideo ? 'button' : 'article'}
        tabIndex={isVideo ? 0 : -1}
        dark={isDark}
        isMobile={isMobile}
      >
        <ImageWrapper isPlaceholder={!isVideo && !thumbnail}>
          {thumbnail && <Thumbnail {...thumbnail} />}
          {!thumbnail &&
            (videoThumbnail ? <Img src={videoThumbnail} /> : <PodcastIcon />)}
          <PlayIcon>
            <Icon icon='icn_playLight' color='#ffffff' size={'3rem'} />
          </PlayIcon>
          <Length>{length}</Length>
        </ImageWrapper>
        <ContentWrapper>
          <Heading>
            {!isVideo && (
              <a href={url} target='_blank' rel='noreferrer'>
                <Title size='xxs' color={isDark ? 'BlueSky' : 'BlueDark'}>
                  {title}
                </Title>
              </a>
            )}
            {isVideo && (
              <Title size='xxs' color={isDark ? 'BlueSky' : 'BlueDark'}>
                {title}
              </Title>
            )}
            <TagsWrapper>
              {!isMobile && isFeatured && (
                <FeaturedTag size='sm'>{t('media.featured')}</FeaturedTag>
              )}
              {displayLanguage && (
                <LanguageTag size='sm' isDark={isDark}>
                  {t(`languages.${node_locale}`)}
                </LanguageTag>
              )}
            </TagsWrapper>
          </Heading>
          <Description size='sm' color={isDark ? 'White' : 'Blue Moon'}>
            {description}
          </Description>
        </ContentWrapper>
      </Media>
    </BlockWrapper>
  );
};

export default MediaItem;

export const MediaItemQuery = graphql`
  fragment MediaItemFragment on ContentfulMediaItem {
    contentful_id
    id
    node_locale
    __typename
    name
    url
    type
    languages
    title
    description {
      description
    }
    thumbnail {
      gatsbyImageData(width: 320, height: 240)
      ...ImageFragment
    }
    length
    topics
    tags
  }
`;
